/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
/* eslint react/jsx-key: "off" */
// ScrollTable wraps each supplied cell in its own keyed table cell.
import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { isAffiliateTestEnvironment } from "../../utils/env";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";
import { LifecycleReviewPanel } from "./LifecycleReviewPanel";
import { ScenarioTab } from "./ScenarioTab";
import { RecoveryPanel } from "./RecoveryPanel";
import { ActivationPanel } from "./ActivationPanel";
import { PointsClawbackPanel } from "./PointsClawbackPanel";
import { AffiliateDirectory } from "./AffiliateDirectory";
import { CodeRevocationAction } from "./CodeRevocationAction";
import { affiliateLabel, affiliateNickname } from "./affiliateIdentity";
import { ConfigurationPanel } from "./ConfigurationPanel";
import { EndedBudgetRetentionPanel } from "./EndedBudgetRetentionPanel";
import { TradeHistoryPanel } from "./TradeHistoryPanel";
import { LifecyclePanel } from "./LifecyclePanel";
import {
  AffiliateOperation,
  AffiliatePayoutPreview,
  AffiliateRates,
  AffiliateTier,
} from "../../interfaces/affiliate";
import {
  affiliateError,
  useAffiliates,
} from "../../hooks/api/admin/useAffiliates";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";

const Page = styled.main`
  height: calc(100vh - 110px);
  min-width: 0;
  overflow: auto;
  padding: 24px;
  box-sizing: border-box;
  color: ${TEXT_COLORS.one};
  h1 {
    font-size: 24px;
    margin: 0;
  }
  h2 {
    font-size: 17px;
    margin: 0 0 12px;
  }
  h3 {
    font-size: 14px;
    margin: 22px 0 10px;
  }
  p {
    color: ${TEXT_COLORS.two};
    line-height: 1.5;
  }
  section {
    margin: 0 0 20px;
    padding: 20px;
    border: 1px solid ${BORDER_COLORS.one};
    border-radius: 10px;
    background: ${BACKGROUND_COLORS.component};
  }
  form {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: 14px;
    padding: 16px;
    margin: 16px 0;
    border-radius: 8px;
    background: ${BACKGROUND_COLORS.four};
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    display: contents;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    min-width: 140px;
    max-width: 100%;
  }
  input,
  select,
  textarea,
  button {
    font: inherit;
    border-radius: 4px;
    border: 1px solid ${BORDER_COLORS.two};
    padding: 10px;
    max-width: 100%;
    box-sizing: border-box;
    background: ${BACKGROUND_COLORS.eight};
    color: inherit;
  }
  input {
    min-width: 150px;
  }
  input[type="checkbox"] {
    min-width: auto;
    width: 16px;
    height: 16px;
    accent-color: ${COLORS.highlight.one};
  }
  textarea {
    min-width: min(440px, 70vw);
    min-height: 88px;
  }
  button {
    cursor: pointer;
    background: ${COLORS.blue.twoSolid};
    border-color: ${COLORS.blue.three};
    min-height: 38px;
    transition: background 120ms ease;
  }
  button:hover:not(:disabled) {
    background: ${COLORS.blue.three};
  }
  button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  summary:focus-visible {
    outline: 2px solid ${COLORS.highlight.one};
    outline-offset: 3px;
  }
  button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 12px;
  }
  th,
  td {
    border-bottom: 1px solid ${BORDER_COLORS.one};
    padding: 14px 12px;
    text-align: left;
    vertical-align: middle;
  }
  th {
    font-weight: 500;
    color: ${TEXT_COLORS.two};
    white-space: nowrap;
  }
  tbody tr:hover {
    background: ${BACKGROUND_COLORS.four};
  }
  a {
    color: ${COLORS.highlight.one};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  code {
    font-size: 11px;
    overflow-wrap: anywhere;
  }
  pre {
    overflow: auto;
    font-size: 11px;
    max-height: 360px;
  }
  summary {
    color: ${TEXT_COLORS.one};
    font-weight: 500;
    cursor: pointer;
    padding: 8px 0;
  }
  [role="alert"] {
    padding: 12px;
    background: #411d2a;
    border-radius: 4px;
    margin: 12px 0;
  }
  @media (max-width: 760px) {
    padding: 16px;
    section {
      padding: 16px;
    }
    form {
      padding: 12px;
    }
    label {
      flex: 1 1 100%;
    }
    label input:not([type="checkbox"]),
    label select,
    label textarea {
      width: 100%;
    }
    input {
      min-width: 0;
    }
  }
  [role="status"] {
    padding: 12px;
    background: #172b3d;
    border-radius: 4px;
    margin: 12px 0;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  h1 {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  p {
    margin: 8px 0 0;
    font-size: 13px;
  }
  @media (max-width: 600px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;
const Environment = styled.span`
  font-size: 10px;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${COLORS.highlight.one};
  padding: 5px 7px;
  border: 1px solid ${COLORS.highlight.three};
  border-radius: 4px;
  background: ${COLORS.highlight.six};
`;
const Overview = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  > div {
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid ${BORDER_COLORS.two};
    background: ${BACKGROUND_COLORS.component};
  }
  span {
    display: block;
    color: ${TEXT_COLORS.two};
    font-size: 12px;
    margin-bottom: 7px;
  }
  strong {
    font-size: 18px;
    font-weight: 500;
  }
  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const Tabs = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${BACKGROUND_COLORS.one};
  display: flex;
  gap: 4px;
  overflow-x: auto;
  border-bottom: 1px solid ${BORDER_COLORS.one};
  margin-bottom: 20px;
  padding: 8px 0;
  button {
    flex: 0 0 auto;
    padding: 10px 14px;
    background: transparent;
    border: 1px solid transparent;
    color: ${TEXT_COLORS.two};
    white-space: nowrap;
  }
  button[aria-selected="true"] {
    color: ${COLORS.highlight.one};
    border-color: ${COLORS.highlight.three};
    background: ${COLORS.highlight.six};
  }
  @media (max-width: 760px) {
    flex-wrap: wrap;
    overflow-x: visible;
    button {
      padding: 9px 11px;
    }
  }
`;
const ActionDisclosure = styled.details`
  margin: 12px 0 20px;
  > summary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid ${COLORS.highlight.three};
    border-radius: 6px;
    padding: 10px 14px;
    color: ${COLORS.highlight.one};
    background: ${COLORS.highlight.six};
    list-style: none;
    font-size: 13px;
  }
  > summary::-webkit-details-marker {
    display: none;
  }
  > summary::before {
    content: "+";
    font-size: 18px;
    line-height: 1;
  }
  &[open] > summary::before {
    content: "−";
  }
  > p {
    font-size: 13px;
    margin-bottom: 0;
  }
`;
const TabDescription = styled.p`
  font-size: 13px;
  margin: 0 0 20px;
`;
const Advanced = styled.details`
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 10px;
  margin-bottom: 20px;
  padding: 12px 20px;
  background: ${BACKGROUND_COLORS.component};
  > section {
    border: 0;
    padding: 16px 0 0;
    margin: 0;
  }
`;
const tabDescriptions: Record<string, string> = {
  affiliates:
    "Find a partner, inspect their dashboard or manage their account.",
  codes: "Referral offers, rates and usage.",
  traders: "Linked accounts and their current trading fees.",
  allowances: "Point balances and funded trading fees.",
  fills: "Recorded fees and rewards by affiliate or trader.",
  payouts: "Review eligible cash and track USDC payments.",
  settings: "Program availability, payments and advanced controls.",
  scenarios:
    "Testnet tools for isolated scenarios, recovery and disposable two-wallet test runs.",
};

function ScrollTable({
  headers,
  rows,
  empty,
}: {
  headers: string[];
  rows: ReactNode[][];
  empty: string;
}) {
  return rows.length ? (
    <div style={{ overflowX: "auto" }}>
      <table>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p>{empty}</p>
  );
}

function Address({ value, nickname }: { value: string; nickname?: string }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column" }}>
      {nickname && (
        <strong style={{ display: "block", marginBottom: 5 }}>
          {nickname}
        </strong>
      )}
      <code title={value}>{value}</code>
    </span>
  );
}
const date = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";
const walletPattern = "0x[a-fA-F0-9]{40}";
const decimalPattern = "[0-9]+(\\.[0-9]{1,6})?";
const rateFields: { key: keyof AffiliateRates; label: string }[] = [
  { key: "perp_taker_bps", label: "Perp taker (bps)" },
  { key: "perp_maker_bps", label: "Perp maker (bps)" },
  { key: "option_taker_bps", label: "Options taker (bps)" },
  { key: "option_maker_bps", label: "Options maker (bps)" },
];
const defaultRates: AffiliateRates = {
  perp_taker_bps: "4",
  perp_maker_bps: "2",
  option_taker_bps: "4.5",
  option_maker_bps: "2.5",
};
const formValues = (event: FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  return new FormData(event.currentTarget);
};
const field = (form: FormData, name: string) =>
  String(form.get(name) || "").trim();
const ratesFrom = (form: FormData): AffiliateRates => ({
  perp_taker_bps: field(form, "perp_taker_bps"),
  perp_maker_bps: field(form, "perp_maker_bps"),
  option_taker_bps: field(form, "option_taker_bps"),
  option_maker_bps: field(form, "option_maker_bps"),
});
function RateInputs({ rates = defaultRates }: { rates?: AffiliateRates }) {
  return (
    <>
      {rateFields.map(({ key, label }) => (
        <label key={key}>
          {label}
          <input
            name={key}
            required
            pattern={decimalPattern}
            inputMode="decimal"
            defaultValue={rates[key]}
          />
        </label>
      ))}
    </>
  );
}
function WalletInput({
  label = "Wallet",
  name = "wallet",
}: {
  label?: string;
  name?: string;
}) {
  return (
    <label>
      {label}
      <input
        name={name}
        list={name === "affiliate" ? "affiliate-owner-options" : undefined}
        required
        pattern={walletPattern}
        placeholder="0x…"
        autoComplete="off"
        style={{ width: 290 }}
      />
    </label>
  );
}

export function Affiliates() {
  const pageRef = useRef<HTMLElement>(null);
  const api = useAffiliates();
  const { data, command: affiliateCommand } = api;
  const testnet = isAffiliateTestEnvironment();
  const [localError, setLocalError] = useState<string>();
  const [preview, setPreview] = useState<AffiliatePayoutPreview>();
  const [refreshPayoutAffiliate, setRefreshPayoutAffiliate] =
    useState<string>();
  const [selectedRatesWallet, setSelectedRatesWallet] = useState("");
  const [approvalTier, setApprovalTier] = useState<AffiliateTier>("VIP3");
  const [approvalAction, setApprovalAction] = useState("approve");
  const [welcomePoints, setWelcomePoints] = useState("");
  const [tab, setTab] = useState(() =>
    testnet && new URLSearchParams(window.location.search).has("scenario")
      ? "scenarios"
      : "affiliates"
  );
  useEffect(() => {
    if (pageRef.current) pageRef.current.scrollTop = 0;
  }, [tab]);
  const tabs = [
    ["affiliates", "Affiliates"],
    ["codes", "Codes"],
    ["traders", "Traders"],
    ["allowances", "Points & budgets"],
    ["fills", "Trades & earnings"],
    ["payouts", "Payouts"],
    ["settings", "Settings"],
    ...(testnet ? [["scenarios", "Test tools"]] : []),
  ];
  const verified = verifiedAffiliateEnvironment(data) && !api.error;
  const locked = api.busy || Boolean(api.retryCommand) || !verified;
  const disabled = locked || !data?.enabled;
  useEffect(() => {
    if (!refreshPayoutAffiliate || locked) return;
    setRefreshPayoutAffiliate(undefined);
    setPreview(undefined);
    affiliateCommand("preview_payout", { affiliate: refreshPayoutAffiliate })
      .then((result) => setPreview(result as AffiliatePayoutPreview))
      .catch((error) => setLocalError(affiliateError(error)));
  }, [refreshPayoutAffiliate, locked, affiliateCommand]);
  const previewBatch = data?.ledger?.batches?.find(
    (batch) => batch.request.id === preview?.payout_id
  );
  const hasEligibleCash =
    preview &&
    /^[0-9]+(\.[0-9]+)?$/.test(preview.cash_eligible_usd) &&
    /[1-9]/.test(preview.cash_eligible_usd);
  const cashPointsOnly = data?.reward_policy === "cash_points_v1";
  const showDaoPreview =
    !cashPointsOnly ||
    Number(preview?.dao_eligible_usd) > 0 ||
    Number(preview?.dao_held_usd) > 0;
  const featureStatus = data
    ? String(data.enabled ? "Enabled" : "Disabled")
    : "Loading status…";
  const weeklyCashStatus = data && (data.weekly_cash ? "Enabled" : "Paused");

  const run = async (
    operation: AffiliateOperation,
    payload: Record<string, unknown>
  ) => {
    setLocalError(undefined);
    if (operation === "preview_payout") setPreview(undefined);
    try {
      const result = await api.command(operation, payload);
      if (operation === "preview_payout")
        setPreview(result as AffiliatePayoutPreview);
      if (operation === "release_payout") {
        setPreview(undefined);
        if (typeof payload.affiliate === "string")
          setRefreshPayoutAffiliate(payload.affiliate);
      }
      return true;
    } catch (error) {
      setLocalError(affiliateError(error));
      return false;
    }
  };
  const submit =
    (
      operation: AffiliateOperation,
      build: (form: FormData) => Record<string, unknown>
    ) =>
    (event: FormEvent<HTMLFormElement>) => {
      const form = formValues(event);
      try {
        run(operation, build(form));
      } catch (error) {
        setLocalError(affiliateError(error));
      }
    };

  if (!api.allowed)
    return (
      <Page>
        <h1>Affiliates</h1>
        <p>
          Affiliate administration is unavailable in this environment. Confirm
          that this admin site is configured for the intended backend.
        </p>
      </Page>
    );

  return (
    <Page ref={pageRef}>
      <Header>
        <div>
          <h1>
            Affiliates{" "}
            <Environment>{testnet ? "TESTNET" : "MAINNET"}</Environment>
          </h1>
          <p>Partners, traders and rewards.</p>
        </div>
        <button type="button" onClick={() => api.mutate()} disabled={api.busy}>
          {api.busy ? "Refreshing…" : "Refresh server state"}
        </button>
      </Header>
      <datalist id="affiliate-owner-options">
        {(verified ? data?.affiliates || [] : []).map((affiliate) => (
          <option key={affiliate.address} value={affiliate.address}>
            {affiliateLabel(data, affiliate.address)}
          </option>
        ))}
      </datalist>
      <Overview aria-label="Affiliate program overview">
        <div>
          <span>Program</span>
          <strong
            data-testid="affiliate-program-status"
            data-loaded={Boolean(data)}
          >
            {featureStatus}
          </strong>
        </div>
        <div>
          <span>Active affiliates</span>
          <strong>
            {data
              ? data.affiliates.filter((affiliate) => !affiliate.terminated_at)
                  .length
              : "—"}
          </strong>
        </div>
        <div>
          <span>Linked traders</span>
          <strong>{data ? data.attachments.length : "—"}</strong>
        </div>
        <div>
          <span>Weekly cash payments</span>
          <strong>{weeklyCashStatus || "—"}</strong>
        </div>
      </Overview>
      {api.error && (
        <div role="alert">
          Cannot read affiliate state: {affiliateError(api.error)}. Changes are
          disabled.
        </div>
      )}
      {data && !verifiedAffiliateEnvironment(data) && (
        <div role="alert">
          The backend did not identify the expected environment. Changes are
          disabled.
        </div>
      )}
      {(localError || api.commandError) && (
        <div role="alert">{localError || api.commandError}</div>
      )}
      {api.receipt && (
        <div role="status">
          Command accepted: <code>{api.receipt.request_id}</code>. Tables show
          server state; acceptance alone does not confirm a completed payment.
        </div>
      )}
      {api.retryCommand && (
        <div role="status">
          The last request has an unconfirmed outcome. Retry uses the same
          request ID to prevent duplicate changes.{" "}
          <button
            type="button"
            disabled={api.busy}
            onClick={() => {
              setLocalError(undefined);
              api
                .retry()
                ?.then((result) => {
                  if ("request" in result)
                    setPreview(result as AffiliatePayoutPreview);
                  if (api.retryCommand?.operation === "release_payout") {
                    setPreview(undefined);
                    const { affiliate } = api.retryCommand.payload;
                    if (typeof affiliate === "string")
                      setRefreshPayoutAffiliate(affiliate);
                  }
                })
                ?.catch((error) => setLocalError(affiliateError(error)));
            }}
          >
            Retry last request
          </button>
        </div>
      )}
      <Tabs role="tablist" aria-label="Affiliate sections">
        {tabs.map(([key, label]) => (
          <button
            type="button"
            role="tab"
            id={`affiliate-tab-${key}`}
            aria-controls={`affiliate-panel-${key}`}
            aria-selected={tab === key}
            tabIndex={tab === key ? 0 : -1}
            key={key}
            onClick={() => setTab(key)}
            onKeyDown={(event) => {
              const index = tabs.findIndex(([id]) => id === key);
              let next = index;
              if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
              else if (event.key === "ArrowLeft")
                next = (index - 1 + tabs.length) % tabs.length;
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = tabs.length - 1;
              else return;
              event.preventDefault();
              setTab(tabs[next][0]);
              document
                .getElementById(`affiliate-tab-${tabs[next][0]}`)
                ?.focus();
            }}
          >
            {label}
          </button>
        ))}
      </Tabs>
      {tabs
        .filter(([key]) => key !== tab)
        .map(([key]) => (
          <div
            key={key}
            role="tabpanel"
            id={`affiliate-panel-${key}`}
            aria-labelledby={`affiliate-tab-${key}`}
            hidden
          />
        ))}
      <div
        role="tabpanel"
        id={`affiliate-panel-${tab}`}
        aria-labelledby={`affiliate-tab-${tab}`}
        tabIndex={0}
      >
        {tab !== "affiliates" && (
          <TabDescription>{tabDescriptions[tab]}</TabDescription>
        )}
        {testnet && tab === "scenarios" && (
          <>
            <RecoveryPanel verifiedTestnet={verified} />
            {testnet && data?.capabilities?.reset_testnet_cohort && (
              <section>
                <h2>Start a fresh two-wallet test</h2>
                <p>
                  Archives this affiliate test run and clears its active
                  referral setup, earnings, points and fee budgets. Existing
                  exchange balances, positions and trade history stay unchanged.
                  The backend rejects resets involving other wallets or payouts.
                  Disable testnet access first. The program stays disabled after
                  the reset.
                </p>
                <form
                  onSubmit={submit("reset_testnet_cohort", (form) => ({
                    affiliate: field(form, "reset_affiliate"),
                    traders: [field(form, "reset_trader")],
                    expected_generation: data.reset_generation,
                    expected_state_digest: data.reset_state_digest,
                  }))}
                >
                  <fieldset
                    disabled={
                      locked ||
                      data.enabled ||
                      !Number.isInteger(data.reset_generation) ||
                      !/^[a-f0-9]{64}$/.test(data.reset_state_digest || "")
                    }
                  >
                    <WalletInput
                      label="Affiliate to reset"
                      name="reset_affiliate"
                    />
                    <WalletInput label="Trader to reset" name="reset_trader" />
                    <label>
                      I want to archive this disposable test run
                      <input type="checkbox" required />
                    </label>
                    <button type="submit">Archive and reset test run</button>
                  </fieldset>
                </form>
              </section>
            )}
            <ScenarioTab />
          </>
        )}

        {tab === "affiliates" && (
          <AffiliateDirectory
            data={verified ? data : undefined}
            locked={locked}
            run={run}
            actions={
              <>
                <ActionDisclosure>
                  <summary>Add affiliate / upgrade tier</summary>
                  <p>Approve a new wallet or upgrade an existing partner.</p>
                  <form
                    onSubmit={(event) => {
                      const form = formValues(event);
                      run(field(form, "action") as "approve" | "upgrade", {
                        affiliate: field(form, "affiliate"),
                        tier: field(form, "tier") as AffiliateTier,
                        ...(data?.capabilities?.approve_welcome_points &&
                        field(form, "action") === "approve" &&
                        welcomePoints.trim()
                          ? { welcome_points: welcomePoints.trim() }
                          : {}),
                      });
                    }}
                  >
                    <fieldset disabled={disabled}>
                      <WalletInput label="Affiliate wallet" name="affiliate" />
                      <label>
                        Tier
                        <select
                          name="tier"
                          value={approvalTier}
                          onChange={(event) => {
                            const tier = event.target.value as AffiliateTier;
                            setApprovalTier(tier);
                            setWelcomePoints(tier === "VIP2" ? "5000" : "");
                          }}
                        >
                          <option>VIP1</option>
                          <option>VIP2</option>
                          <option>VIP3</option>
                        </select>
                      </label>
                      <label>
                        Action
                        <select
                          name="action"
                          value={approvalAction}
                          onChange={(event) =>
                            setApprovalAction(event.target.value)
                          }
                        >
                          <option value="approve">Approve new affiliate</option>
                          <option value="upgrade">
                            Upgrade existing affiliate
                          </option>
                        </select>
                      </label>
                      {data?.capabilities?.approve_welcome_points &&
                        approvalAction === "approve" && (
                          <label>
                            Initial welcome points
                            <input
                              name="welcome_points"
                              inputMode="decimal"
                              pattern={decimalPattern}
                              value={welcomePoints}
                              onChange={(event) =>
                                setWelcomePoints(event.target.value)
                              }
                              placeholder="No initial grant"
                            />
                          </label>
                        )}
                      <button type="submit">Apply affiliate tier</button>
                    </fieldset>
                  </form>
                </ActionDisclosure>
                {data && (
                  <Advanced>
                    <summary>Manage affiliate tier and status</summary>
                    <LifecyclePanel
                      data={data}
                      disabled={disabled}
                      onCommand={run}
                    />
                    {data.lifecycle_policy === "launch_v1" && (
                      <LifecycleReviewPanel
                        data={data}
                        disabled={locked}
                        command={api.command}
                        terminationOnly
                      />
                    )}
                  </Advanced>
                )}
              </>
            }
          />
        )}

        {tab === "codes" && (
          <section>
            <h2>Referral codes</h2>
            <ActionDisclosure>
              <summary>Create code</summary>
              <form
                onSubmit={submit("create_code", (form) => ({
                  affiliate: field(form, "affiliate"),
                  code: field(form, "code"),
                  rates: ratesFrom(form),
                  use_cap: Number(field(form, "use_cap")),
                  expires_at: new Date(field(form, "expires_at")).toISOString(),
                }))}
              >
                <fieldset disabled={disabled}>
                  <WalletInput label="Code owner" name="affiliate" />
                  <label>
                    Code
                    <input name="code" required autoComplete="off" />
                  </label>
                  <RateInputs />
                  <label>
                    Use cap
                    <input
                      type="number"
                      min="1"
                      step="1"
                      max={Number.MAX_SAFE_INTEGER}
                      name="use_cap"
                      required
                      defaultValue="10"
                    />
                  </label>
                  <label>
                    Code expires (local time)
                    <input type="datetime-local" name="expires_at" required />
                  </label>
                  <button type="submit">Create affiliate code</button>
                </fieldset>
              </form>
            </ActionDisclosure>
            <ScrollTable
              headers={[
                "Code",
                "Owner",
                "Uses / cap",
                "Expires",
                "Perp taker / maker",
                "Options taker / maker",
                "Status / action",
              ]}
              rows={(data?.codes || []).map((code) => [
                code.code,
                <Address
                  value={code.affiliate}
                  nickname={affiliateNickname(data, code.affiliate)}
                />,
                `${code.uses} / ${code.use_cap}`,
                date(code.expires_at),
                `${code.rates.perp_taker_bps} / ${code.rates.perp_maker_bps} bps`,
                `${code.rates.option_taker_bps} / ${code.rates.option_maker_bps} bps`,
                <CodeRevocationAction
                  key={code.code}
                  code={code}
                  disabled={locked || data?.capabilities?.revoke_code !== true}
                  run={run}
                />,
              ])}
              empty="No affiliate codes."
            />
          </section>
        )}

        {tab === "traders" && (
          <>
            <section>
              <h2>Linked traders</h2>
              <ActionDisclosure>
                <summary>Link trader</summary>
                <p>
                  Linking is permanent. Traders can also preview and join an
                  offer from the Affiliates page on the exchange.
                </p>
                <form
                  onSubmit={submit("attach", (form) => ({
                    wallet: field(form, "wallet"),
                    code: field(form, "code"),
                  }))}
                >
                  <fieldset disabled={disabled}>
                    <WalletInput label="Trader wallet" />
                    <label>
                      Affiliate code
                      <input name="code" required />
                    </label>
                    <button type="submit">Link trader permanently</button>
                  </fieldset>
                </form>
              </ActionDisclosure>
              <ScrollTable
                headers={[
                  "Wallet",
                  "Affiliate / code",
                  "Attached / cashback stops",
                  "Perp taker / maker",
                  "Options taker / maker",
                  "Remaining zero-fee budget",
                ]}
                rows={(data?.attachments || []).map((account) => [
                  <Address value={account.wallet} />,
                  <>
                    <Address
                      value={account.affiliate}
                      nickname={affiliateNickname(data, account.affiliate)}
                    />
                    <br />
                    {account.code}
                  </>,
                  <>
                    {date(account.attached_at)}
                    <br />
                    {date(account.cashback_cutoff)}
                  </>,
                  `${account.rates.perp_taker_bps} / ${account.rates.perp_maker_bps} bps`,
                  `${account.rates.option_taker_bps} / ${account.rates.option_maker_bps} bps`,
                  `$${account.allowance_remaining_usd}`,
                ])}
                empty="No attached wallets."
              />
            </section>
            <section>
              <h2>Scheduled fee changes</h2>
              <ActionDisclosure>
                <summary>Change trader fees</summary>
                <p>
                  Reductions apply now; increases apply after seven days. Any
                  fee change locks further edits for seven days.
                </p>
                <form
                  key={selectedRatesWallet}
                  onSubmit={submit("set_rates", (form) => ({
                    wallet: field(form, "wallet"),
                    rates: ratesFrom(form),
                  }))}
                >
                  <fieldset disabled={disabled}>
                    <label>
                      Attached wallet
                      <select
                        name="wallet"
                        required
                        value={selectedRatesWallet}
                        onChange={(event) =>
                          setSelectedRatesWallet(event.target.value)
                        }
                      >
                        <option value="">Choose a wallet</option>
                        {data?.attachments?.map((account) => (
                          <option key={account.wallet} value={account.wallet}>
                            {account.wallet}
                          </option>
                        ))}
                      </select>
                    </label>
                    <RateInputs
                      rates={
                        data?.attachments?.find(
                          (account) => account.wallet === selectedRatesWallet
                        )?.rates
                      }
                    />
                    <button type="submit">Schedule wallet fees</button>
                  </fieldset>
                </form>
              </ActionDisclosure>
              <ScrollTable
                headers={["Wallet", "Fee", "Current", "Next", "Effective"]}
                rows={(data?.attachments || []).flatMap((account) =>
                  (
                    [
                      "perp_taker",
                      "perp_maker",
                      "option_taker",
                      "option_maker",
                    ] as const
                  )
                    .filter((key) => account[key]?.next_rate_bps !== undefined)
                    .map((key) => [
                      <Address value={account.wallet} />,
                      key.replaceAll("_", " "),
                      `${account[key].rate_bps} bps`,
                      `${account[key].next_rate_bps} bps`,
                      date(account[key].next_effective_at),
                    ])
                )}
                empty="No pending fee changes."
              />
            </section>
          </>
        )}

        {tab === "allowances" && (
          <>
            <section>
              <h2>Point balances</h2>
              <ActionDisclosure>
                <summary>Grant points</summary>
                <p>
                  Welcome points are available immediately. Earned points vest
                  after seven days and expire 180 days after earning. Four
                  available points fund $1 in waived trading fees.
                </p>
                <form
                  onSubmit={submit("grant_welcome", (form) => ({
                    affiliate: field(form, "affiliate"),
                    points: field(form, "points"),
                  }))}
                >
                  <fieldset disabled={disabled}>
                    <WalletInput label="Grant recipient" name="affiliate" />
                    <label>
                      Welcome points
                      <input
                        name="points"
                        required
                        pattern={decimalPattern}
                        inputMode="decimal"
                      />
                    </label>
                    <button type="submit">Grant welcome points</button>
                  </fieldset>
                </form>
              </ActionDisclosure>
              <ScrollTable
                headers={[
                  "Affiliate",
                  "Origin",
                  "Remaining points",
                  "Available",
                  "Expires",
                ]}
                rows={(data?.ledger?.points || []).map((lot) => [
                  <Address
                    value={lot.affiliate_id}
                    nickname={affiliateNickname(data, lot.affiliate_id)}
                  />,
                  lot.kind,
                  lot.remaining,
                  date(lot.available_at),
                  date(lot.expires_at),
                ])}
                empty="No point lots. Trade through the exchange to earn points, or grant welcome points above."
              />
            </section>
            <section>
              <h2>Zero-fee budgets</h2>
              <ActionDisclosure>
                <summary>Fund budget</summary>
                <p>
                  Four points fund $1 in trading fees. Leave the end blank to
                  last until spent. At a chosen end, unused funds are forfeited
                  and are not returned as points.
                </p>
                <form
                  onSubmit={submit("allocate_allowance", (form) => ({
                    affiliate: field(form, "affiliate"),
                    wallet: field(form, "wallet"),
                    dollars: field(form, "dollars"),
                    ...(field(form, "end_at")
                      ? {
                          end_at: new Date(field(form, "end_at")).toISOString(),
                        }
                      : {}),
                  }))}
                >
                  <fieldset disabled={disabled}>
                    <WalletInput label="Funding affiliate" name="affiliate" />
                    <WalletInput label="Zero-fee wallet" />
                    <label>
                      Zero-fee budget (USD)
                      <input
                        name="dollars"
                        required
                        pattern={decimalPattern}
                        inputMode="decimal"
                      />
                    </label>
                    <label>
                      Optional end (local time)
                      <input name="end_at" type="datetime-local" />
                    </label>
                    <button type="submit">Fund zero-fee budget</button>
                  </fieldset>
                </form>
              </ActionDisclosure>
              <ScrollTable
                headers={[
                  "Budget",
                  "Wallet",
                  "Funded",
                  "Recorded unspent",
                  "Ends",
                ]}
                rows={(data?.ledger?.allowances || []).map((allowance) => [
                  allowance.id,
                  <Address value={allowance.wallet} />,
                  `$${allowance.original_usd}`,
                  `$${allowance.remaining_usd}`,
                  allowance.end_at ? date(allowance.end_at) : "Until spent",
                ])}
                empty="No funded zero-fee budgets."
              />
            </section>
            {data?.capabilities?.clawback_points && (
              <Advanced>
                <summary>Claw back points</summary>
                <PointsClawbackPanel disabled={disabled} run={run} />
              </Advanced>
            )}
          </>
        )}

        {tab === "fills" && (
          <TradeHistoryPanel state={verified ? data : undefined} />
        )}

        {tab === "payouts" && (
          <>
            <section>
              <h2>Payment history</h2>
              <ScrollTable
                headers={[
                  "Batch",
                  "Affiliate",
                  "USDC",
                  "DAO value / AEVO in batch",
                  "Status",
                  "Payment reference",
                  "Paid at",
                  "Resume existing transfer",
                ]}
                rows={(data?.ledger?.batches || []).map((batch) => [
                  batch.request.id,
                  <Address
                    value={batch.request.affiliate_id}
                    nickname={affiliateNickname(
                      data,
                      batch.request.affiliate_id
                    )}
                  />,
                  `$${batch.cash_usd}`,
                  `$${batch.dao_usd} / ${batch.dao_tokens}`,
                  batch.status === "reserved" &&
                  data?.cash_payments?.[batch.request.id]?.submitted
                    ? "submitted"
                    : batch.status,
                  batch.payment_id || "—",
                  date(batch.paid_at),
                  <button
                    type="button"
                    aria-label={`Resume USDC transfer ${batch.request.id}`}
                    disabled={
                      disabled ||
                      !data?.capabilities?.release_payout ||
                      batch.status === "paid" ||
                      !/^[0-9]+(\.[0-9]+)?$/.test(batch.cash_usd) ||
                      !/[1-9]/.test(batch.cash_usd)
                    }
                    onClick={() =>
                      run("release_payout", {
                        affiliate: batch.request.affiliate_id,
                        payout_id: batch.request.id,
                      })
                    }
                  >
                    {batch.status === "paid" ? "Paid" : "Resume USDC transfer"}
                  </button>,
                ])}
                empty="No payout batches. Payments are not marked completed when you submit an approval."
              />
              <details>
                <summary>About resumed payments</summary>
                <p>
                  Resume retries the existing transfer; it does not create a new
                  payout.
                </p>
              </details>
            </section>
            <section>
              <h2>Review a payout</h2>
              <details>
                <summary>Payout rules</summary>
                <p>
                  Release eligible cash commission to the affiliate’s internal
                  Aevo USDC balance. Cash stays on hold for seven days; this
                  page cannot accelerate the holdback.
                  {cashPointsOnly
                    ? " New DAO AEVO rewards and AEVO payouts are disabled. Historical DAO entitlements remain recorded and unpaid."
                    : " DAO AEVO entitlement remains accrued and unpaid until its payment network and rules are agreed."}
                </p>
              </details>
              <form
                onSubmit={submit("preview_payout", (form) => ({
                  affiliate: field(form, "affiliate"),
                }))}
              >
                <fieldset disabled={disabled}>
                  <WalletInput label="Payout affiliate" name="affiliate" />
                  <button type="submit">Preview payout</button>
                </fieldset>
              </form>
              {preview && (
                <div>
                  <h3>
                    Preview for{" "}
                    <Address
                      value={preview.request.affiliate_id}
                      nickname={affiliateNickname(
                        data,
                        preview.request.affiliate_id
                      )}
                    />
                  </h3>
                  <p>Cutoff: {date(preview.request.cutoff)}</p>
                  <ScrollTable
                    headers={[
                      "Eligible USDC",
                      "USDC under holdback",
                      ...(showDaoPreview
                        ? [
                            cashPointsOnly
                              ? "Historical DAO value (unpaid)"
                              : "Accrued DAO value (unpaid)",
                            "Held DAO value (unpaid)",
                            "AEVO quantity (not released)",
                          ]
                        : []),
                    ]}
                    rows={[
                      [
                        `$${preview.cash_eligible_usd}`,
                        `$${preview.cash_held_usd}`,
                        ...(showDaoPreview
                          ? [
                              `$${preview.dao_eligible_usd}`,
                              `$${preview.dao_held_usd}`,
                              `${preview.dao_tokens}${
                                preview.token_quantity_estimate
                                  ? " (estimate)"
                                  : ""
                              }`,
                            ]
                          : []),
                      ],
                    ]}
                    empty=""
                  />
                  {(preview.decision_needed || []).map((decision) => (
                    <p key={decision}>{decision}</p>
                  ))}
                  {preview.explicit_payout_hold && <p>Payout is on hold.</p>}
                  <p>
                    Destination: the internal Aevo balance of{" "}
                    <Address
                      value={preview.request.affiliate_id}
                      nickname={affiliateNickname(
                        data,
                        preview.request.affiliate_id
                      )}
                    />
                    . This releases USDC only.
                  </p>
                  {!hasEligibleCash && (
                    <p>No USDC is eligible for release at this cutoff.</p>
                  )}
                  {previewBatch && (
                    <p>
                      This payout already has a backend batch. Its status is
                      shown below. Resume an unpaid transfer from that existing
                      batch.
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={
                      disabled ||
                      !data?.capabilities?.release_payout ||
                      !preview.payout_id ||
                      preview.explicit_payout_hold ||
                      !hasEligibleCash ||
                      Boolean(previewBatch)
                    }
                    onClick={() => {
                      if (preview.payout_id)
                        run("release_payout", {
                          affiliate: preview.request.affiliate_id,
                          payout_id: preview.payout_id,
                        });
                    }}
                  >
                    Release {testnet ? "testnet " : ""}USDC
                  </button>
                </div>
              )}
              {!data?.capabilities?.release_payout && (
                <p>
                  Payment release is unavailable on this backend. A preview does
                  not move funds.
                </p>
              )}
            </section>
            {verified && data?.lifecycle_policy === "launch_v1" && (
              <Advanced>
                <summary>Payout holds and adjustments</summary>
                <LifecycleReviewPanel
                  data={data}
                  disabled={locked}
                  command={api.command}
                />
              </Advanced>
            )}
          </>
        )}
        {tab === "settings" && (
          <>
            <ConfigurationPanel data={data} locked={locked} run={run} />
            <Advanced>
              <summary>Budget history retention</summary>
              <EndedBudgetRetentionPanel
                data={data}
                locked={locked}
                run={run}
              />
            </Advanced>
            {data && (
              <Advanced>
                <summary>Advanced: program preparation</summary>
                <ActivationPanel data={data} locked={locked} run={run} />
              </Advanced>
            )}
            {data?.decisions?.length ? (
              <Advanced>
                <summary>Program rules and pending decisions</summary>
                <p>
                  Backend policy values are shown below. Provisional values are
                  marked for review before changing program settings.
                </p>
                <ul>
                  {data.decisions.map((decision) => (
                    <li key={decision.key}>
                      <code>{decision.key}</code>: {decision.value}{" "}
                      {decision.provisional ? "(provisional)" : "(confirmed)"}
                    </li>
                  ))}
                </ul>
              </Advanced>
            ) : null}
            {verified && data && (
              <Advanced>
                <summary>Backend diagnostics</summary>
                <p>
                  Policy version: {data.policy_version || "—"}. Export the
                  current server state for an audit or support investigation.
                </p>
                <button
                  type="button"
                  disabled={!verified || api.busy}
                  onClick={() => {
                    if (!data || !verified) return;
                    const url = URL.createObjectURL(
                      new Blob([JSON.stringify(data, null, 2)], {
                        type: "application/json",
                      })
                    );
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `affiliate-${
                      testnet ? "testnet" : "mainnet"
                    }-state-${new Date()
                      .toISOString()
                      .replace(/[:.]/g, "-")}.json`;
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  }}
                >
                  Download backend state
                </button>
                <details>
                  <summary>Inspect complete backend state</summary>
                  <pre>{JSON.stringify(data, null, 2)}</pre>
                </details>
              </Advanced>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
